import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { axiosInstance } from "../../services/axiosInstance";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { Select, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { endpoints } from "../../services/endpoint";
import "./promptsdisplay.css";
import AddPromptsPage from "../Adding/addPrompts/addPrompts";
import EditPromptsPage from "../Editing/editPrompts/editprompt";
import { fetchPollyVoices, synthesizeSpeech } from "../../services/pollyService";

const PromptsPage = ({ prompts = [] }) => {
  const [promptsState, setPrompts] = useState(prompts);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingRowId, setLoadingRowId] = useState(null);
  const [playingRowId, setPlayingRowId] = useState(null);
  const [audio, setAudio] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoices, setSelectedVoices] = useState({});
  const [playingAudioData, setPlayingAudioData] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingprompts, setEditingPrompts] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [copiedArn, setCopiedArn] = useState(null);
  const [deletingId, setDeletingId] = useState(null); // Track which specific prompt is being deleted
  const [isDeleting, setIsDeleting] = useState(false); // Track if any delete operation is in progress
  const [actionClicked, setActionClicked] = useState(null); // "yes" | "no" | null
  const instanceAlias = localStorage.getItem("instanceName");
  const customerAWSAccountId = localStorage.getItem("CustomerAWSAccountId");
  const region = localStorage.getItem("Region");
  const instanceId = localStorage.getItem("InstanceId");
  const EncryptedInstanceId = localStorage.getItem("EncryptedId");
  const username = localStorage.getItem("username");
  const email = localStorage.getItem("Email");
  const [promptContexts, setPromptContexts] = useState({});

  useEffect(() => {
    fetchPollyVoices().then(setVoices);
  }, []);

  const fetchPromptContexts = async () => {
    try {
      const res = await fetch(
        "https://grj87qmm19.execute-api.us-east-1.amazonaws.com/Test/ListPromptContext",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "ListContext" }),
        }
      );

      const data = await res.json();

      if (data.status === "Success") {
        const contextMap = {};
        data.data.forEach((item) => {
          contextMap[item.PromptName] = item;
        });

        setPromptContexts(contextMap);
      }
    } catch (err) {
      console.error("Error fetching prompt contexts:", err);
    }
  };

  useEffect(() => {
    fetchPromptContexts();
  }, []);

  useEffect(() => {
    if (
      voices.length > 0 &&
      promptsState.length > 0 &&
      Object.keys(promptContexts).length > 0
    ) {
      const initial = {};

      promptsState.forEach((p) => {
        const id = p.Id || p.id;

        // Get context for this prompt
        const context = promptContexts[p.name];

        // Extract voice ID (before " - ")
        let voiceFromAPI = context?.Voice || null;

        // Fallback to Joanna if null
        const finalVoice =
          voices.find((v) => v.id === voiceFromAPI)?.id ||
          voices.find((v) => v.name === voiceFromAPI)?.id || // fallback if name stored
          voices.find((v) => v.id === "Joanna")?.id ||
          voices[0]?.id;

        initial[id] = finalVoice;
      });

      setSelectedVoices(initial);
    }
  }, [voices, promptsState, promptContexts]);

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value);
    setCopiedArn(value);
    setTimeout(() => setCopiedArn(null), 2000);
  };

  const handlePlayAudio = async (prompt) => {
    try {
      if (audio) {
        audio.pause();
        audio.src = "";         
        audio.load();            
        setAudio(null);
        setPlayingRowId(null);
        setPlayingAudioData(null);
      }

      const promptId = prompt.Id || prompt.id;
      setLoadingRowId(promptId);

      const selectedVoice = selectedVoices[promptId] || "Joanna";
      const context = promptContexts[prompt.name];

      // If we have ContextText, synthesize using Polly dynamically
      if (context && context.ContextText) {
        const base64Audio = await synthesizeSpeech(context.ContextText, selectedVoice);
        const byteChars = atob(base64Audio);
        const byteNumbers = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([byteNumbers], { type: "audio/wav" });
        const url = URL.createObjectURL(blob);

        setPlayingRowId(promptId);
        setPlayingAudioData({
          url,
          promptId,
          promptName: prompt.name,
        });
        return;
      }

      const payload = {
        action: "play",
        InstanceAlias: instanceAlias,
        ID: EncryptedInstanceId,
        awsAccount: customerAWSAccountId,
        region,
        promptName: prompt.name,
        voiceId: selectedVoice, 
      };

      const response = await fetch(endpoints.promptsDisplay.promptlist, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      const url = JSON.parse(data.body)?.promptFile?.PromptPresignedUrl;

      if (url) {
        setPlayingRowId(promptId);
        setPlayingAudioData({
          url,
          promptId,
          promptName: prompt.name,
        });
      }
    } catch (error) {
      console.error("Error playing audio:", error);
    } finally {
      setLoadingRowId(null);
    }
  };

  const handleCloseAudio = () => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setAudio(null);
    }
    setPlayingRowId(null);
    setPlayingAudioData(null);
  };

  const openDeleteDialog = (prompt) => {
    setPromptToDelete(prompt);
    setShowDeleteDialog(true);
  };

  const fetchPrompts = async () => {
    const body = {
      action: "list",
      ID: EncryptedInstanceId,
      awsAccount: customerAWSAccountId,
      region,
      InstanceAlias: instanceAlias,
    };

    try {
      const response = await axiosInstance.post(endpoints.promptsDisplay.promptlist, body);

      if (response.data.statusCode === 200) {
        const parsed = JSON.parse(response.data.body);
        const promptList = parsed.prompts;

        if (Array.isArray(promptList)) {
          return promptList.map((item) => ({
            name: item.Name || "Unnamed Prompt",
            id: item.Id || "-",
            arn: item.ARN || "-",
            description: item.Description || "No description",
            tags: item.Tags && Object.keys(item.Tags).length > 0 ? item.Tags : {},
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching prompts list:", error);
    }

    return [];
  };

  const handleConfirmDelete = async (prompt) => {
    if (!prompt) return;

    // Get the consistent ID
    const promptId = prompt.Id || prompt.id;

    // Set loading states
    setDeletingId(promptId);
    setIsDeleting(true);

    try {
      const requestPayload = {
        InstanceAlias: instanceAlias,
        awsAccount: customerAWSAccountId,
        ID: EncryptedInstanceId,
        region,
        action: "delete",
        BucketName: `${customerAWSAccountId}-${instanceAlias}-prompts`,
        Name: prompt.name,
        UserName: username,
        Email: email,
      };

      await axiosInstance.post(endpoints.promptPlay.promptlist, requestPayload);

      const updatedPrompts = await fetchPrompts();
      setPrompts(updatedPrompts);
      setShowDeleteDialog(false);
      setPromptToDelete(null);
    } catch (error) {
      console.error("Error deleting prompt:", error);
      // You might want to show an error message to the user here
    } finally {
      setDeletingId(null);
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    // Don't allow canceling if delete is in progress
    if (isDeleting) return;

    setShowDeleteDialog(false);
    setPromptToDelete(null);
  };

  const waitForContextUpdate = async (promptName, retries = 5, delay = 500) => {
    for (let i = 0; i < retries; i++) {
      const res = await fetch(
        "https://grj87qmm19.execute-api.us-east-1.amazonaws.com/Test/ListPromptContext",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "ListContext" }),
        }
      );

      const data = await res.json();

      if (data.status === "Success") {
        const found = data.data.find(
          (item) => item.PromptName === promptName
        );

        if (found) {
          const contextMap = {};
          data.data.forEach((item) => {
            contextMap[item.PromptName] = item;
          });

          setPromptContexts(contextMap);
          return;
        }
      }

      await new Promise((res) => setTimeout(res, delay));
    }

    console.warn("Context not updated in time");
  };

  const handleProfileAdded = async (updatedList) => {
    setPrompts(updatedList || []);
    setShowAddForm(false);

    const lastAddedPrompt = updatedList?.[updatedList.length - 1];

    if (lastAddedPrompt?.name) {
      await waitForContextUpdate(lastAddedPrompt.name);
    }
  };

  const handlePromptsUpdated = async (updatedList) => {
    setPrompts(updatedList || []);
    setEditingPrompts(null);

    await fetchPromptContexts();
  };

  const filteredPrompts = useMemo(() => {
    return (
      promptsState?.filter((p) =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase())
      ) || []
    );
  }, [promptsState, searchTerm]);

  if (showAddForm) {
    return (
      <AddPromptsPage
        onClose={() => setShowAddForm(false)}
        onProfileAdded={handleProfileAdded}
        instanceAlias={instanceAlias}
        instanceId={instanceId}
        customerAWSAccountId={customerAWSAccountId}
        region={region}
      />
    );
  }

  if (editingprompts) {
    return (
      <EditPromptsPage
        prompt={editingprompts}
        onClose={() => setEditingPrompts(null)}
        onUpdate={handlePromptsUpdated}
        instanceAlias={instanceAlias}
        instanceId={instanceId}
        customerAWSAccountId={customerAWSAccountId}
        region={region}
      />
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" className="phone-number-title">
        Prompts List
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          placeholder="Search by name..."
          className="search-textfield"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2, width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          className="addprompt-button"
          onClick={() => setShowAddForm(true)}
        >
          Add Prompt
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {filteredPrompts.length > 0 ? (
        <Paper elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>ARN</strong></TableCell>
                <TableCell className="tablebodydescription-prompts"><strong>Description</strong></TableCell>
                <TableCell><strong>Tags</strong></TableCell>
                <TableCell><strong>Play</strong></TableCell>
                <TableCell><strong>Delete</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrompts.map((prompt) => {
                const promptId = prompt.Id || prompt.id;
                const isCurrentlyDeleting = deletingId === promptId;

                return (
                  <React.Fragment key={promptId}>
                    <TableRow
                      sx={{
                        opacity: isCurrentlyDeleting ? 0.5 : 1,
                        pointerEvents: isCurrentlyDeleting ? 'none' : 'auto'
                      }}
                    >
                      <TableCell>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (!isCurrentlyDeleting) {
                              setEditingPrompts(prompt);
                            }
                          }}
                          style={{
                            textDecoration: "underline",
                            color: isCurrentlyDeleting ? "#999" : "#1976d2",
                            cursor: isCurrentlyDeleting ? "default" : "pointer",
                          }}
                        >
                          {prompt.name}
                        </a>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Tooltip title={copiedArn === prompt.arn ? "Copied" : "Copy ARN"}>
                            <IconButton
                              onClick={() => !isCurrentlyDeleting && handleCopy(prompt.arn)}
                              size="small"
                              disabled={isCurrentlyDeleting}
                            >
                              <ContentCopyOutlinedIcon
                                fontSize="small"
                                color={copiedArn === prompt.arn ? "primary" : "inherit"}
                              />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="body2" sx={{ ml: 1, wordBreak: "break-all" }}></Typography>
                        </Box>
                      </TableCell>

                      <TableCell className="tablebodydescription-prompts">{prompt.description || "-"}</TableCell>
                      <TableCell className="tablebodytags-prompts">
                        {prompt.tags && Object.keys(prompt.tags).length > 0 ? (
                          <div className="tags-container-prompts">
                            {Object.entries(prompt.tags).map(([key, value]) => (
                              <div className="tag-box-prompts" key={key}>
                                <strong>{key}:</strong> {value}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span>No Tags</span>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>

                          {/* 🔽 Voice Dropdown */}
                          <Select
                            size="small"
                            value={selectedVoices[promptId] || "Joanna"}
                            onChange={(e) =>
                              setSelectedVoices((prev) => ({
                                ...prev,
                                [promptId]: e.target.value,
                              }))
                            }
                            sx={{ minWidth: 120, height: 36 }}
                            disabled={isCurrentlyDeleting}
                          >
                            {voices.map((voice) => (
                              <MenuItem key={voice.id} value={voice.id}>
                                {voice.name}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* ▶️ Play Button */}
                          {isCurrentlyDeleting ? (
                            <CircularProgress size={24} sx={{ color: "#999" }} />
                          ) : loadingRowId === promptId ? (
                            <CircularProgress size={24} />
                          ) : playingRowId === promptId ? (
                            <IconButton color="primary" onClick={handleCloseAudio}>
                              <KeyboardArrowUpIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="default"
                              onClick={() => handlePlayAudio(prompt)}
                              disabled={isCurrentlyDeleting}
                            >
                              <PlayCircleFilledIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isCurrentlyDeleting ? (
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <CircularProgress size={24} color="error" />
                            <Typography variant="caption" sx={{ ml: 1, color: 'error.main' }}>
                              Deleting...
                            </Typography>
                          </Box>
                        ) : (
                          <IconButton
                            color="error"
                            onClick={() => openDeleteDialog(prompt)}
                            disabled={isDeleting} // Disable all delete buttons when any delete is in progress
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* Audio player row */}
                    {(() => {
                      const shouldShowPlayer = playingRowId === promptId &&
                        playingAudioData?.promptId === promptId &&
                        !isCurrentlyDeleting;

                      return shouldShowPlayer ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <audio
                                controls
                                src={playingAudioData.url}
                                autoPlay
                                onPause={handleCloseAudio}
                                onEnded={handleCloseAudio}
                              >
                                Your browser does not support the audio element.
                              </audio>
                              <IconButton
                                component="a"
                                href={playingAudioData.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={playingAudioData.promptName + ".wav"}
                                style={{ marginLeft: "10px" }}
                              >
                                <PlayCircleFilledIcon className="prompts-play-color" />
                              </IconButton>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null;
                    })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Typography>No prompts found.</Typography>
      )}

      <Dialog
        open={showDeleteDialog}
        onClose={actionClicked === "yes" ? null : handleCancelDelete} // disable close if yes is deleting
        disableEscapeKeyDown={actionClicked === "yes"} // lock escape during yes-delete
        PaperProps={{
          sx: {
            opacity: actionClicked === "yes" ? 0.8 : 1,
            pointerEvents: actionClicked === "yes" ? "none" : "auto"
          }
        }}
      >
        <DialogTitle sx={{ opacity: actionClicked === "yes" ? 0.6 : 1 }}>
          Delete Prompt
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ opacity: actionClicked === "yes" ? 0.6 : 1 }}>
            Are you sure you want to delete the prompt{" "}
            <strong>{promptToDelete?.name || ""}</strong>?

          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ pointerEvents: "auto" }}>
          {/* No Button */}
          <Button
            onClick={() => {
              setActionClicked("no");
              handleCancelDelete();
              setActionClicked(null); 
            }}
            variant="outlined"
            color="inherit"
            disabled={actionClicked !== null && actionClicked !== "no"}
            sx={{
              opacity: actionClicked === "yes" ? 0.3 : 1,
              minWidth: "80px",
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "#999",
                borderColor: "#ddd"
              }
            }}
          >
            {actionClicked === "no" ? <CircularProgress size={18} /> : "No"}
          </Button>

          {/* Yes Button */}
          <Button
            onClick={async () => {
              setActionClicked("yes");
              await handleConfirmDelete(promptToDelete);
              setShowDeleteDialog(false);
              setActionClicked(null); 
            }}
            color="error"
            variant="contained"
            disabled={actionClicked !== null && actionClicked !== "yes"}
            startIcon={
              actionClicked === "yes" ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{
              opacity: actionClicked === "yes" ? 0.7 : 1,
              minWidth: "120px",
              "&.Mui-disabled": {
                backgroundColor: "#ffebee",
                color: "#f44336"
              }
            }}
          >
            {actionClicked === "yes" ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PromptsPage;